import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getErrorMessage, getLastApiError, isApiError } from '@/lib/api';
import { hasFeedbackListener, openFeedback } from '@/lib/feedbackBus';

type ToastVariant = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  /**
   * Ação exibida no toast. Em toasts de erro o padrão é "Reportar" (abre o
   * feedback já como problema); passe `null` para não exibir ação.
   */
  action?: ToastAction | null;
}

interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
  title?: string;
  action?: ToastAction;
}

interface ToastApi {
  success: (message: string, title?: string, options?: ToastOptions) => void;
  error: (message: string, title?: string, options?: ToastOptions) => void;
  info: (message: string, title?: string, options?: ToastOptions) => void;
  /** Helper padrão para erros de API: mensagem amigável + "Reportar" com o correlationId */
  apiError: (error: unknown, fallback?: string, title?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TOAST_DURATION_MS = 5000;
/** Toast com ação fica mais tempo para dar tempo de clicar */
const TOAST_WITH_ACTION_DURATION_MS = 9000;

function buildReportAction(message: string, explicitCorrelationId?: string): ToastAction | undefined {
  if (!hasFeedbackListener()) return undefined;
  // Toasts antigos passam só a mensagem: recupera o correlationId do erro de API correspondente
  const lastError = getLastApiError();
  const correlationId = explicitCorrelationId ?? (lastError?.message === message ? lastError.correlationId : undefined);
  return {
    label: 'Reportar',
    onClick: () => openFeedback({ tipo: 'bug', erroMensagem: message, correlationId }),
  };
}

const VARIANT_STYLES: Record<ToastVariant, { icon: typeof Info; className: string; label: string }> = {
  success: { icon: CheckCircle2, className: 'text-success', label: 'Sucesso' },
  error: { icon: AlertCircle, className: 'text-danger', label: 'Erro' },
  info: { icon: Info, className: 'text-info', label: 'Aviso' },
};

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const { icon: Icon, className, label } = VARIANT_STYLES[toast.variant];

  useEffect(() => {
    const duration = toast.action ? TOAST_WITH_ACTION_DURATION_MS : TOAST_DURATION_MS;
    const timer = window.setTimeout(() => onDismiss(toast.id), duration);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.action, onDismiss]);

  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      className="pointer-events-auto flex w-full items-start gap-3 rounded-xl border border-line bg-elevated p-4 shadow-elevation-4 animate-slide-up"
    >
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', className)} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{toast.title ?? label}</p>
        <p className="mt-0.5 text-sm text-muted">{toast.message}</p>
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action?.onClick();
              onDismiss(toast.id);
            }}
            className="mt-2 rounded-md text-sm font-semibold text-brand-600 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-brand-300"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="rounded-md p-1 text-muted transition-colors hover:bg-ink/5 hover:text-ink"
        aria-label="Fechar aviso"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((variant: ToastVariant, message: string, title?: string, action?: ToastAction) => {
    const id = nextId.current++;
    setToasts((current) => [...current.slice(-3), { id, variant, message, title, action }]);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message, title, options) => push('success', message, title, options?.action ?? undefined),
      error: (message, title, options) =>
        push('error', message, title, options?.action === undefined ? buildReportAction(message) : options.action ?? undefined),
      info: (message, title, options) => push('info', message, title, options?.action ?? undefined),
      apiError: (error, fallback, title) => {
        const message = getErrorMessage(error, fallback);
        push('error', message, title, buildReportAction(message, isApiError(error) ? error.correlationId : undefined));
      },
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-4 bottom-4 z-[100] flex flex-col items-end gap-2 sm:inset-x-auto sm:right-6 sm:w-96"
      >
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast precisa estar dentro de <ToastProvider>.');
  return context;
}
